import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Group, Image as KonvaImage, Line, Transformer, Rect } from 'react-konva';
import { useNativeImage } from '../../hooks/useNativeImage';
import ContainerHoverMenu from './ContainerHoverMenu';

/**
 * MapContainer - Interactive container for images and drawings
 * Each container represents a single image with its associated drawings
 * All elements move together as a single unit using Konva.Group
 */
const MapContainer = ({
  container,
  isSelected,
  onUpdate,
  onSelect,
  onDrawingAdd,
  onDrawingUpdate,
  onDrawingDelete,
  isDrawingMode,
  activeTool,
  onHoverStart,
  onHoverEnd,
  showTransformer = true,
  viewport = null,
  isAutoLocked = false, // New prop for auto-lock state
  isEditMode = true, // New prop for edit/play mode
  // New props for integrated hover menu
  showHoverMenu = false,
  onMenuAction = null
}) => {
  const { image, loading, error } = useNativeImage(container.image.src);
  const groupRef = useRef();
  const transformerRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const [isTransforming, setIsTransforming] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef();
  
  // Check if container is locked (manual, auto, or play mode)
  const isLocked = container.locked || isAutoLocked || !isEditMode;
  
  // Update transformer when selection changes or lock status changes
  useEffect(() => {
    // Use requestAnimationFrame to ensure the DOM is ready
    const updateTransformer = () => {
      if (isSelected && transformerRef.current && groupRef.current && showTransformer && !isLocked) {
        // Double-check that the group is attached to a layer
        const layer = groupRef.current.getLayer();
        if (layer) {
          transformerRef.current.nodes([groupRef.current]);
          transformerRef.current.getLayer().batchDraw();
        } else {
          // If layer is not ready, try again on next frame
          requestAnimationFrame(updateTransformer);
        }
      } else if (transformerRef.current) {
        // Clear transformer if locked or not selected
        transformerRef.current.nodes([]);
        const layer = transformerRef.current.getLayer();
        if (layer) {
          layer.batchDraw();
        }
      }
    };

    // Use requestAnimationFrame to ensure everything is rendered
    requestAnimationFrame(updateTransformer);
  }, [isSelected, showTransformer, isLocked]);

  // Additional effect to update transformer when image loads
  useEffect(() => {
    if (image && isSelected && !isLocked && showTransformer) {
      // Small delay to ensure the image is fully rendered
      const timer = setTimeout(() => {
        if (transformerRef.current && groupRef.current) {
          const layer = groupRef.current.getLayer();
          if (layer) {
            transformerRef.current.nodes([groupRef.current]);
            transformerRef.current.getLayer().batchDraw();
          }
        }
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [image, isSelected, isLocked, showTransformer]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    const timeoutRef = hoverTimeoutRef; // Store the ref itself to avoid stale closure
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Add window focus handler for proper synchronization
  useEffect(() => {
    const handleWindowFocus = () => {
             // When window regains focus, force container to recalculate positions
       if (groupRef.current) {
         // Force the group to recalculate its transform
         const group = groupRef.current;
         group.cache();
         group.clearCache();
         
         // Reset any stuck states
         if (isDragging) {
           setIsDragging(false);
         }
         if (isTransforming) {
           setIsTransforming(false);
         }
         
         // Force redraw
         const layer = group.getLayer();
         if (layer) {
           layer.batchDraw();
         }
       }
      
      // Also sync transformer if it exists
      if (transformerRef.current && isSelected) {
        setTimeout(() => {
          transformerRef.current.forceUpdate();
        }, 10);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleWindowFocus();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSelected, isDragging, isTransforming]);
  
  // Handle container drag
  const handleDragStart = useCallback((e) => {
    if (isLocked) {
      e.evt.preventDefault();
      return;
    }
    setIsDragging(true);
    setIsHovering(false); // Hide menu during drag
    onSelect?.(container.id);
  }, [isLocked, container.id, onSelect]);
  
  const handleDragEnd = useCallback((e) => {
    if (isLocked) return;
    
    const node = e.target;
    const newX = node.x();
    const newY = node.y();
    
    setIsDragging(false);
    
    onUpdate?.(container.id, {
      x: newX,
      y: newY
    });
  }, [isLocked, container.id, onUpdate]);
  
  // Handle container transformation (resize, rotate)
  const handleTransformStart = useCallback(() => {
    setIsTransforming(true);
    setIsHovering(false); // Hide menu during transform
  }, []);
  
  const handleTransformEnd = useCallback(() => {
    if (isLocked || !transformerRef.current) return;
    
    const node = transformerRef.current.nodes()[0];
    if (!node) return;
    
    setIsTransforming(false);
    
    // Get transform values and ensure they're valid
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const rotation = node.rotation();
    
    // Prevent negative scales (flipping) and ensure minimum scale
    const minScale = 0.1;
    const maxScale = 10;
    const validScaleX = Math.max(minScale, Math.min(maxScale, Math.abs(scaleX)));
    const validScaleY = Math.max(minScale, Math.min(maxScale, Math.abs(scaleY)));
    
    // Normalize rotation to 0-360 range
    const normalizedRotation = ((rotation % 360) + 360) % 360;
    
    onUpdate?.(container.id, {
      x: node.x(),
      y: node.y(),
      scaleX: validScaleX,
      scaleY: validScaleY,
      rotation: normalizedRotation
    });
  }, [isLocked, container.id, onUpdate]);
  
  // Handle mouse interactions for drawing
  const handleMouseDown = useCallback((e) => {
    // Allow drawing even when auto-locked (only prevent manual interaction)
    if (!isDrawingMode || !activeTool) return;
    
    // Don't start drawing if we're dragging or transforming
    if (isDragging || isTransforming) return;
    
    const stage = e.target.getStage();
    const pointerPosition = stage.getPointerPosition();
    
    // Convert global coordinates to container-relative coordinates
    const group = groupRef.current;
    const transform = group.getAbsoluteTransform().copy().invert();
    const relativePos = transform.point(pointerPosition);
    
    onDrawingAdd?.(container.id, {
      tool: activeTool,
      startPoint: relativePos,
      points: [relativePos.x, relativePos.y]
    });
  }, [isDrawingMode, activeTool, container.id, onDrawingAdd, isDragging, isTransforming]);
  
  // Handle container selection
  const handleClick = useCallback((e) => {
    // Don't select if we're in drawing mode (but allow selection with SELECT tool)
    if (isDrawingMode && activeTool !== 'select') return;
    
    e.cancelBubble = true;
    onSelect?.(container.id);
  }, [isDrawingMode, activeTool, container.id, onSelect]);
  
    // Handle hover for menu
  const handleMouseEnter = useCallback((e) => {
    if (isDragging || isTransforming) return;
    // Allow hover even in drawing mode when using SELECT tool
    if (isDrawingMode && activeTool !== 'select') return;
    
    setIsHovering(true);
    
    // Still call the old callback for backward compatibility
    const group = groupRef.current;
    if (group) {
      const stage = group.getStage();
      if (!stage) return;
      
      const transform = group.getAbsoluteTransform();
      const topRightLocal = { x: container.image.width - 10, y: 10 };
      const topRightGlobal = transform.point(topRightLocal);
      
      const stageTransform = stage.getAbsoluteTransform().copy().invert();
      const stagePos = stageTransform.point(topRightGlobal);
      
      onHoverStart?.(container.id, stagePos.x, stagePos.y);
    }
  }, [container.id, container.image.width, onHoverStart, isDragging, isTransforming, isDrawingMode, activeTool]);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    onHoverEnd?.();
  }, [onHoverEnd]);
  
  // Bound box function for transformer to maintain aspect ratio
  const boundBoxFunc = useCallback((oldBox, newBox) => {
    // Calculate aspect ratio
    const ratio = container.image.originalAspectRatio || (container.image.width / container.image.height);
    
    // Prevent negative dimensions
    if (newBox.width <= 0 || newBox.height <= 0) {
      return oldBox;
    }
    
    // Calculate which scale is larger to maintain aspect ratio
    const scaleX = newBox.width / oldBox.width;
    const scaleY = newBox.height / oldBox.height;
    
    // Use the larger scale to maintain aspect ratio and prevent flipping
    const scale = Math.max(Math.abs(scaleX), Math.abs(scaleY));
    
    // Apply the scale while maintaining aspect ratio
    const newWidth = oldBox.width * scale;
    const newHeight = newWidth / ratio;
    
    // Ensure minimum size
    const minSize = 10;
    if (newWidth < minSize || newHeight < minSize) {
      return oldBox;
    }
    
    return {
      ...newBox,
      width: newWidth,
      height: newHeight
    };
  }, [container.image.originalAspectRatio, container.image.width, container.image.height]);
  
  // Don't render if image failed to load
  if (error) {
    console.warn(`Failed to load container image: ${container.id}`, error);
    return null;
  }
  
  // Don't render if image is still loading
  if (loading || !image) {
    return null;
  }
  
  // Viewport culling - don't render if outside viewport
  if (viewport) {
    const containerBounds = {
      left: container.x,
      top: container.y,
      right: container.x + (container.image.width * container.scaleX),
      bottom: container.y + (container.image.height * container.scaleY)
    };
    
    const isVisible = !(
      containerBounds.right < viewport.x ||
      containerBounds.left > viewport.x + viewport.width ||
      containerBounds.bottom < viewport.y ||
      containerBounds.top > viewport.y + viewport.height
    );
    
    if (!isVisible) {
      return null;
    }
  }
  
  return (
    <>
      <Group
        ref={groupRef}
        x={container.x}
        y={container.y}
        scaleX={container.scaleX}
        scaleY={container.scaleY}
        rotation={container.rotation}
        draggable={!isLocked}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        opacity={isDragging ? 0.7 : 1}
      >
        {/* Invisible hover area - larger than image to prevent menu flickering */}
        <Rect
          x={-20}
          y={-20}
          width={container.image.width + 40}
          height={container.image.height + 40}
          fill="transparent"
          listening={true}
        />
        
        {/* Main image */}
        <KonvaImage
          image={image}
          width={container.image.width}
          height={container.image.height}
          listening={false}
        />
        
        {/* Container drawings (relative coordinates) */}
        {container.drawings && Array.isArray(container.drawings) && container.drawings.map(drawing => (
          <Line
            key={drawing.id}
            points={drawing.points}
            stroke={drawing.color || '#ff0000'}
            strokeWidth={drawing.strokeWidth || 2}
            lineCap="round"
            lineJoin="round"
            globalCompositeOperation={drawing.tool === 'eraser' ? 'destination-out' : 'source-over'}
            listening={false} // Drawings don't need to be interactive
          />
        ))}
        
        {/* Selection indicator */}
        {isSelected && !showTransformer && (
          <Rect
            x={0}
            y={0}
            width={container.image.width}
            height={container.image.height}
            stroke="#0066ff"
            strokeWidth={2}
            dash={[5, 5]}
            listening={false}
          />
        )}
        
        {/* Lock indicators */}
        
        {/* Manual lock indicator (user locked) */}
        {container.locked && (
          <Rect
            x={0}
            y={0}
            width={container.image.width}
            height={container.image.height}
            stroke="rgba(255, 0, 0, 0.8)"
            strokeWidth={3}
            dash={[8, 4]}
            listening={false}
          />
        )}
        
        {/* Auto-lock indicator (FOW active) */}
        {isAutoLocked && !container.locked && (
          <Rect
            x={0}
            y={0}
            width={container.image.width}
            height={container.image.height}
            stroke="rgba(255, 165, 0, 0.8)"
            strokeWidth={3}
            dash={[10, 5]}
            listening={false}
          />
        )}
        
        {/* Integrated hover menu - moves with container */}
        {(isHovering || showHoverMenu) && isEditMode && !isDragging && !isTransforming && (
          <ContainerHoverMenu
            x={container.image.width / 2}
            y={-45 * (1 / (viewport?.scale || 1))}
            visible={true}
            onAction={onMenuAction}
            containerLocked={container.locked}
            containerName={container.name}
            onHoverStart={() => setIsHovering(true)}
            onHoverEnd={() => setIsHovering(false)}
            stageScale={viewport?.scale || 1}
          />
        )}
      </Group>
      
      {/* Transformer for resizing/rotating */}
      {isSelected && showTransformer && !isLocked && (
        <Transformer
          ref={transformerRef}
          boundBoxFunc={boundBoxFunc}
          onTransformStart={handleTransformStart}
          onTransformEnd={handleTransformEnd}
          enabledAnchors={[
            'top-left',
            'top-right',
            'bottom-left',
            'bottom-right',
            'top-center',
            'bottom-center',
            'middle-left',
            'middle-right'
          ]}
          rotateEnabled={true}
          borderStroke="#0066ff"
          borderStrokeWidth={2}
          anchorStroke="#0066ff"
          anchorStrokeWidth={2}
          anchorFill="white"
          anchorSize={8}
          flipEnabled={false}
          keepRatio={true}
          centeredScaling={false}
        />
      )}
    </>
  );
};

export default MapContainer; 